import type { FC, FormEvent, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import {
    ArrowRight,
    Check,
    LoaderCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { firstName } from '@/lib/format'
import { isEmail } from '@/lib/validation'
import { useGlassSheen } from '@/hooks/use-glass-sheen'
import { CERCLE_BENEFITS, DEFAULT_INTERESTS } from '@/constants/landing'

const EMPTY_FORM = {
    fullName: '',
    email: '',
    city: '',
    note: '',
    company: ''
}

type MembershipResult = { ok: true } | { ok: false, message: string }

const inputClass = 'h-12 w-full rounded-2xl border border-ivory/15 bg-ivory/[0.06] px-4 text-[0.95rem] text-ivory placeholder:text-ivory/35 transition focus:border-champagne/60 focus:bg-ivory/[0.09] focus:outline-none focus:ring-4 focus:ring-gold/15'
const labelClass = 'mb-1.5 block text-[0.68rem] uppercase tracking-[0.2em] text-ivory/70'

interface CercleSectionProps {
    interests: string[]
}

export const CercleSection: FC<CercleSectionProps> = ({ interests }): ReactNode => {
    const options = interests.length > 0 ? interests : DEFAULT_INTERESTS
    const [form, setForm] = useState(EMPTY_FORM)
    const [selected, setSelected] = useState<string[]>([])
    const [consent, setConsent] = useState(false)
    const [sending, setSending] = useState(false)
    const [sent, setSent] = useState(false)
    const [error, setError] = useState('')
    const [applicant, setApplicant] = useState('')
    const successRef = useRef<HTMLDivElement>(null)
    const panel = useGlassSheen()

    useEffect(() => {
        if (sent) successRef.current?.focus()
    }, [sent])

    const update = (field: keyof typeof EMPTY_FORM) => (value: string) => {
        setForm((current) => ({ ...current, [field]: value }))
    }

    const toggleInterest = (interest: string) => {
        setSelected((current) => current.includes(interest)
            ? current.filter((item) => item !== interest)
            : [...current, interest])
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (sending) return

        if (form.fullName.trim().length < 2) {
            setError("Please tell us your full name.")
            return
        }

        if (!isEmail(form.email.trim())) {
            setError("Please enter a valid email address.")
            return
        }

        if (!consent) {
            setError("Please agree to be contacted about your application.")
            return
        }

        setSending(true)
        setError('')

        let message = "We could not send your application just now. Please try again in a moment."

        try {
            const response = await fetch('/api/membership', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, interests: selected, consent })
            })

            const result = (await response.json().catch(() => null)) as MembershipResult | null

            if (response.ok && result?.ok) {
                setApplicant(firstName(form.fullName))
                setForm(EMPTY_FORM)
                setSelected([])
                setConsent(false)
                setSent(true)
                setSending(false)
                return
            }

            if (result && !result.ok && result.message) message = result.message
        }
        catch {
            // Network failure: keep the general message.
        }

        setError(message)
        setSending(false)
    }

    return (
        <section id='cercle' aria-labelledby='cercle-title' className='relative isolate overflow-hidden bg-ink py-24 text-ivory sm:py-32'>
            <div aria-hidden='true' className='absolute inset-0 -z-10 overflow-hidden'>
                <Image
                    src='/images/products/ambre-nocturne.jpg'
                    alt=''
                    fill
                    sizes='30vw'
                    className='scale-125 object-cover opacity-60 blur-[70px] saturate-[1.35]'
                />

                <div className='absolute inset-0 bg-linear-to-b from-ink/85 via-ink/45 to-ink/90' />
                <div className='ambient-orb -left-32 top-[18%] size-[30rem] bg-gold/30' />
                <div className='ambient-orb -bottom-24 -right-24 size-[34rem] bg-gold-deep/45 [animation-delay:-10s]' />
            </div>

            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.02fr] lg:items-center lg:gap-20'>
                <div>
                    <p className='glass glass-dark inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-[0.66rem] uppercase tracking-[0.28em] text-champagne'>
                        <span aria-hidden='true' className='size-1.5 rotate-45 bg-champagne' />
                        By application only
                    </p>

                    <h2
                        id='cercle-title'
                        className='mt-8 font-heading text-[clamp(3rem,6.4vw,5.8rem)] font-light leading-[0.95] tracking-[-0.02em]'
                    >
                        Le Cercle <em className='text-gilded-light'>Aveline</em>
                    </h2>

                    <p className='mt-6 max-w-lg text-lg leading-relaxed text-ivory/70'>
                        A private circle of collectors in forty countries. Membership is complimentary, and every application is read personally by our client relations team in Paris.
                    </p>

                    <ul className='mt-10 grid gap-6 sm:grid-cols-2'>
                        {CERCLE_BENEFITS.map((benefit) => {
                            const Icon = benefit.icon

                            return (
                                <li key={benefit.title} className='flex gap-4'>
                                    <span className='glass glass-dark flex size-11 shrink-0 items-center justify-center rounded-full'>
                                        <Icon className='size-5 text-champagne' strokeWidth={1.4} />
                                    </span>

                                    <div>
                                        <p className='font-medium text-ivory'>
                                            {benefit.title}
                                        </p>

                                        <p className='mt-1 text-sm leading-relaxed text-ivory/60'>
                                            {benefit.text}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </div>

                <div
                    ref={panel.ref}
                    onPointerMove={panel.onPointerMove}
                    onPointerLeave={panel.onPointerLeave}
                    className='glass glass-dark rounded-[2.5rem] p-6 sm:p-10'
                >
                    {sent ? (
                        <div
                            ref={successRef}
                            role='status'
                            tabIndex={-1}
                            className='flex min-h-[30rem] flex-col items-center justify-center text-center focus:outline-none'
                        >
                            <span className='flex size-16 items-center justify-center rounded-full bg-linear-to-br from-gold to-champagne text-espresso animate-in zoom-in duration-500'>
                                <Check className='size-7' strokeWidth={1.5} />
                            </span>

                            <p className='mt-8 font-heading text-4xl font-light'>
                                {applicant ? `Thank you, ${applicant}.` : "Thank you."}
                            </p>

                            <p className='mt-4 max-w-sm leading-relaxed text-ivory/65'>
                                Your application is with our client relations team in Paris. You will hear from us personally within three working days.
                            </p>

                            <a
                                href='#collection'
                                className='mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-ivory/10 px-6 text-[0.74rem] uppercase tracking-[0.18em] ring-1 ring-ivory/20 transition-colors hover:bg-ivory/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-champagne'
                            >
                                Return to the collection
                                <ArrowRight className='size-4' strokeWidth={1.5} />
                            </a>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate>
                            <p className='font-heading text-3xl font-light sm:text-4xl'>
                                Request an invitation
                            </p>

                            <p className='mt-2 text-sm text-ivory/60'>
                                It takes a minute. We reply personally within three working days.
                            </p>

                            <div className='mt-8 grid gap-4 sm:grid-cols-2'>
                                <div className='sm:col-span-2'>
                                    <label htmlFor='cercle-name' className={labelClass}>Full name</label>
                                    <input
                                        id='cercle-name'
                                        autoComplete='name'
                                        required
                                        value={form.fullName}
                                        onChange={(event) => update('fullName')(event.target.value)}
                                        className={inputClass}
                                        placeholder="Your name"
                                    />
                                </div>

                                <div>
                                    <label htmlFor='cercle-email' className={labelClass}>Email</label>
                                    <input
                                        id='cercle-email'
                                        type='email'
                                        autoComplete='email'
                                        required
                                        value={form.email}
                                        onChange={(event) => update('email')(event.target.value)}
                                        className={inputClass}
                                        placeholder="you@example.com"
                                    />
                                </div>

                                <div>
                                    <label htmlFor='cercle-city' className={labelClass}>City</label>
                                    <input
                                        id='cercle-city'
                                        autoComplete='address-level2'
                                        value={form.city}
                                        onChange={(event) => update('city')(event.target.value)}
                                        className={inputClass}
                                        placeholder="Paris, London, Tokyo"
                                    />
                                </div>
                            </div>

                            <fieldset className='mt-6'>
                                <legend className={labelClass}>Your interests</legend>

                                <div className='mt-1 flex flex-wrap gap-2'>
                                    {options.map((option) => {
                                        const isSelected = selected.includes(option)

                                        return (
                                            <button
                                                key={option}
                                                type='button'
                                                aria-pressed={isSelected}
                                                onClick={() => toggleInterest(option)}
                                                className={cn(
                                                    'flex h-10 items-center gap-2 rounded-full px-4 text-sm transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-champagne',
                                                    isSelected
                                                        ? 'bg-champagne text-espresso'
                                                        : 'bg-ivory/5 text-ivory/80 ring-1 ring-ivory/15 hover:bg-ivory/10'
                                                )}
                                            >
                                                {isSelected && <Check className='size-4' strokeWidth={1.75} />}
                                                {option}
                                            </button>
                                        )
                                    })}
                                </div>
                            </fieldset>

                            <div className='mt-6'>
                                <label htmlFor='cercle-note' className={labelClass}>A piece you love (optional)</label>
                                <textarea
                                    id='cercle-note'
                                    rows={3}
                                    value={form.note}
                                    onChange={(event) => update('note')(event.target.value)}
                                    className={cn(inputClass, 'h-auto resize-none py-3')}
                                    placeholder="An heirloom, a piece from the collection, or one you have been searching for"
                                />
                            </div>

                            <div aria-hidden='true' className='hidden'>
                                <label htmlFor='cercle-company'>Company</label>
                                <input
                                    id='cercle-company'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={form.company}
                                    onChange={(event) => update('company')(event.target.value)}
                                />
                            </div>

                            <label htmlFor='cercle-consent' className='mt-6 flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ivory/70'>
                                <input
                                    id='cercle-consent'
                                    type='checkbox'
                                    checked={consent}
                                    onChange={(event) => setConsent(event.target.checked)}
                                    className='mt-1 size-4 shrink-0 accent-gold'
                                />
                                I agree to be contacted by a client advisor about my application.
                            </label>

                            {error && (
                                <p role='alert' className='mt-5 rounded-2xl bg-destructive/20 px-4 py-3 text-sm text-champagne ring-1 ring-destructive/40'>
                                    {error}
                                </p>
                            )}

                            <button
                                type='submit'
                                disabled={sending}
                                className='mt-7 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-gold via-champagne to-gold text-[0.76rem] uppercase tracking-[0.2em] text-espresso shadow-[0_20px_40px_-20px_rgba(212,180,124,0.6)] transition-[filter,transform] duration-300 hover:-translate-y-0.5 hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-champagne'
                            >
                                {sending ? (
                                    <>
                                        <LoaderCircle className='size-4 animate-spin' strokeWidth={1.5} />
                                        Sending
                                    </>
                                ) : (
                                    <>
                                        Request an invitation
                                        <ArrowRight className='size-4' strokeWidth={1.5} />
                                    </>
                                )}
                            </button>

                            <p className='mt-4 text-center text-xs text-ivory/45'>
                                Membership is complimentary. Your details are only used to answer your application.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    )
}
